import React, { useEffect, useRef, useState } from 'react';

const HEADLINES = [
  { tag: 'AI', color: 'bg-teal-700', title: 'Research lab claims new reasoning benchmark record', source: 'Net Digest', time: '2h' },
  { tag: 'EU', color: 'bg-amber-700', title: 'Regulators finalize rules for high-risk AI systems', source: 'Wire Desk', time: '4h' },
  { tag: 'GPU', color: 'bg-slate-600', title: 'Chipmaker unveils next-gen inference hardware', source: 'Signal Brief', time: '6h' },
  { tag: 'OSS', color: 'bg-emerald-800', title: 'Open-source model matches closed frontier scores', source: 'AI Weekly', time: '9h' },
  { tag: 'API', color: 'bg-indigo-700', title: 'Cloud provider cuts inference pricing by a third', source: 'Net Digest', time: '13h' },
];

function jitter(value, spread, min, max) {
  const next = value + (Math.random() * spread * 2 - spread);
  return Math.min(max, Math.max(min, Math.round(next)));
}

function Metric({ label, value, color }) {
  return (
    <div className="flex flex-1 flex-col gap-1.5">
      <span className="text-[11px] text-[#8A8676]">{label}</span>
      <span className="text-[15px] font-bold text-[#1C1B19]">{value}%</span>
      <div className="h-1 rounded-full bg-[#EDEBE4] overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

// The decoy landing screen — a generic news + status reader. The real chat
// lives behind a horizontal swipe so it never appears on screen by default.
export default function HomeFeed({ onReveal }) {
  const [metrics, setMetrics] = useState({ load: 42, cpu: 58, ram: 71 });
  const swipeStart = useRef(null);
  const dragStart = useRef(null);
  const wheelAccum = useRef(0);
  const wheelResetTimer = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setMetrics((m) => ({
        load: jitter(m.load, 4, 15, 90),
        cpu: jitter(m.cpu, 5, 20, 95),
        ram: jitter(m.ram, 2, 40, 90),
      }));
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Generous on purpose: a swipe only needs to lean horizontal, not be perfectly straight,
  // and a short trackpad two-finger swipe (wheel deltaX) works the same way.
  const trySwipe = (dx, dy) => {
    if (dx < -40 && Math.abs(dx) > Math.abs(dy)) onReveal();
  };

  const handleWheel = (e) => {
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    wheelAccum.current += e.deltaX;
    clearTimeout(wheelResetTimer.current);
    wheelResetTimer.current = setTimeout(() => { wheelAccum.current = 0; }, 300);
    if (wheelAccum.current > 60) onReveal();
  };

  return (
    <div
      className="flex h-full flex-col bg-[#FAF9F5] relative overflow-hidden"
      style={{ touchAction: 'pan-y' }}
      onTouchStart={(e) => { swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
      onTouchMove={(e) => {
        if (!swipeStart.current) return;
        const dx = e.touches[0].clientX - swipeStart.current.x;
        const dy = e.touches[0].clientY - swipeStart.current.y;
        if (dx < -40 && Math.abs(dx) > Math.abs(dy)) { onReveal(); swipeStart.current = null; }
      }}
      onTouchEnd={(e) => {
        if (!swipeStart.current) return;
        trySwipe(e.changedTouches[0].clientX - swipeStart.current.x, e.changedTouches[0].clientY - swipeStart.current.y);
        swipeStart.current = null;
      }}
      onTouchCancel={() => { swipeStart.current = null; }}
      onMouseDown={(e) => { dragStart.current = { x: e.clientX, y: e.clientY }; }}
      onMouseMove={(e) => {
        if (!dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (dx < -40 && Math.abs(dx) > Math.abs(dy)) { onReveal(); dragStart.current = null; }
      }}
      onMouseUp={(e) => {
        if (!dragStart.current) return;
        trySwipe(e.clientX - dragStart.current.x, e.clientY - dragStart.current.y);
        dragStart.current = null;
      }}
      onMouseLeave={() => { dragStart.current = null; }}
      onWheel={handleWheel}
    >
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between px-5"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 24px)', paddingBottom: '12px' }}>
        <span className="text-[19px] font-bold text-[#1C1B19]" style={{ fontFamily: 'Georgia, serif' }}>Brief</span>
        <div className="h-7 w-7 rounded-full bg-[#E4E1D8]" />
      </div>

      {/* Fake system status */}
      <div className="mx-4 mb-4 flex-shrink-0 rounded-xl border border-[#E4E1D8] bg-white px-4 py-3.5 flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-bold text-[#44413A] tracking-wide">SYSTEM STATUS</span>
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Operational
          </span>
        </div>
        <div className="flex gap-4">
          <Metric label="Load" value={metrics.load} color="bg-[#2B6B5E]" />
          <Metric label="CPU" value={metrics.cpu} color="bg-[#2B6B5E]" />
          <Metric label="RAM" value={metrics.ram} color="bg-[#B8863C]" />
        </div>
        <span className="text-[10px] text-[#A6A296]">Updated just now</span>
      </div>

      <span className="flex-shrink-0 px-5 pb-1 text-[13px] font-bold text-[#1C1B19]">Latest</span>

      {/* News list */}
      <div className="flex-1 overflow-y-auto">
        {HEADLINES.map((item, i) => (
          <div key={i} className="flex gap-3 px-5 py-3 border-b border-[#F0EEE7]">
            <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white ${item.color}`}>
              {item.tag}
            </div>
            <div className="min-w-0 flex flex-col gap-1">
              <span className="text-[13px] font-semibold leading-snug text-[#1C1B19]">{item.title}</span>
              <span className="text-[11px] text-[#A6A296]">{item.source} · {item.time}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
